import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isNil, pathOr, isEmpty } from "ramda";
import { v4 as uuidv4 } from "uuid";

import { isLangKorean } from "@/utils/i18n";
import { toTitleCase } from "@/utils/conversions";
import { getTechnologyStyleBlock } from "@/utils/data/productGroupings";

import ROUTES from "@/constants/routes";
import { SIDE } from "@/constants/rfid-tags";

import Codification from "@/components/Codification";
import Button from "@/components/Button";
import ReviewStars from "@/components/ReviewStars";
import ButtonIcon from "@/components/ButtonIcon";
import ShoeHeading from "@/components/ShoeHeading";
import Block from "@/components/Block";
import Typography from "@/components/Typography";
import Image from "@/components/Image";

import { updateVisible, clearActive, clearQty, clearPrice } from "@/store/shoes/shoes.actions";
import { useActiveShoes, useActiveShoe, useTableLocation } from "@/store/shoes/shoes.hooks";
import { useFlippedOrientation, useLowGPU } from "@/store/ui/ui.hooks";
import { setFlippedOrientation } from "@/store/ui/ui.actions";
import { removeTag } from "@/store/rfid/rfid.actions";
import { setLatestAction, setShoesModal } from "@/store/ui/ui.actions";

import { DEFAULT_DURATION } from "@/constants/transition";
import { getMoveY, rotateInfinite, fadeMove } from "@/animations";
import { useFilterIDs } from "@/store/filters/filters.hooks";

import getPulseData from "@/utils/data/getPulseData";

const ProductSportPulse = ({ activeShoe }) => {
	const { t } = useTranslation();
	const lowGPU = useLowGPU();
	const filterIDs = useFilterIDs();
	const dispatch = useDispatch();
	const tableLocation = useTableLocation();
	const city = tableLocation.data.json_meta || { sportpulse_id: null };

	const [isQR, setisQR] = useState(false);
	const [showSportPulse, setShowSportPulse] = useState(false);
	const [avgPace, setAvgPace] = useState(null);
	const [totalDistance, setTotalDistance] = useState(null);

	const checkShowSportPulse = () => {
		if (process.env.REACT_APP_LOCATION === "china") return false;
		let performance = false;

		try {
			const category = pathOr(null, ["meta", "category"], activeShoe);
			performance = category === filterIDs.runningID; // || category === filterIDs.trainingID;
		} catch (e) {
			sentryLogException("No Performance for Language", e);
		}
		// console.log(performance, activeShoe);

		return performance;
	};

	const handleSetQR = () => {
		setisQR(!isQR);
		if (!isQR) dispatch(setLatestAction({ type: "show_nike_app_download_qr", id: uuidv4() }));
	};

	useEffect(() => {
		setShowSportPulse(checkShowSportPulse());
		setAvgPace(getPulseData(activeShoe, "avg_pace"));
		setTotalDistance(getPulseData(activeShoe, "total_distance"));
	}, [activeShoe]);

	if (!isNil(avgPace) && showSportPulse)
		return (
			<Block className="c-block--inset c-block--floating-content" onClick={handleSetQR}>
				<Block.Header>
					<Typography tag="p" size="2xs">
						{t("titles.nike_run_club")}
					</Typography>
				</Block.Header>

				<Block.Content className="u-flex u-jc-center u-ai-center u-text-c">
					<AnimatePresence>
						{isQR && (
							<motion.div
								className="c-block__content-inner"
								variants={fadeMove}
								initial="hidden"
								animate="show"
								exit="exit"
							>
								<Image
									src={
										city.sportpulse_id !== "Seoul"
											? "https://nikeqr.io/?url=https%3A%2F%2Fnikerunning.sng.link%2FA6sko%2Fq213%3F_dl%3Dnikerunclub%253A%252F%252Fx-callback-url%252Faudioguidedrun%252Flanding%26_smtype%3D3%26cp%3Dwpcn"
											: "https://nikeqr.io/?url=https%3A%2F%2Fwww.nike.com%2Fkr%2Fko_kr%2Fc%2Frunning%2Faudio-guide-run"
									}
									className="u-px-13"
								/>
							</motion.div>
						)}
					</AnimatePresence>
					<AnimatePresence initial={false}>
						{!isQR && (
							<motion.div
								className="c-block__content-inner"
								variants={fadeMove}
								initial="hidden"
								animate="show"
								exit="exit"
							>
								<Codification
									text={avgPace}
									typography={{
										primary: true,
										size: "3xl",
										tag: "span",
										uppercase: true,
									}}
									characterSwitchAmount={4}
									characterNextTrigger={2}
									characterTimeout={20}
									lowGPU={lowGPU}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</Block.Content>

				<Block.Footer className="u-flex u-jc-between u-ac-end">
					<AnimatePresence>
						{isQR && (
							<motion.div variants={fadeMove} initial="hidden" animate="show" exit="exit">
								<Typography tag="p" size="2xs">
									{t("titles.nike_run_club_qr")}
								</Typography>
							</motion.div>
						)}
					</AnimatePresence>
					<AnimatePresence initial={false}>
						{!isQR && (
							<motion.div variants={fadeMove} initial="hidden" animate="show" exit="exit">
								<Typography tag="p" size="2xs">
									{t("generic.average_pace")}
								</Typography>
							</motion.div>
						)}
					</AnimatePresence>
					<ButtonIcon className="c-icon--size-3" icon="plus" />
				</Block.Footer>
			</Block>
		);
	else return null;
};

export default React.memo(ProductSportPulse);
